import { Component, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { AuthService } from '../../../auth/auth.service';

/**
 * Non-blocking nudge for accounts still using the onboarding default password
 * (username@123). The flag comes from the server (User.mustChangePassword,
 * re-synced on every password login) and is exposed on GET /auth/me.
 *
 * Rendered as the first child of `.mainwrapper` rather than inside
 * <app-header>: the header is `position: fixed`, so anything appended after it
 * lands underneath the fixed bar and is invisible. Sitting in the content flow
 * puts the banner directly below the header on every page, and it pushes the
 * page down instead of covering it.
 */
@Component({
  selector: 'app-password-prompt',
  standalone: true,
  imports: [RouterModule],
  template: `
    @if (show) {
    <div
      class="alert alert-warning d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3"
      role="alert"
    >
      <div>
        <i class="bi bi-shield-exclamation me-2"></i>
        You're still signing in with the default password. Please set a
        password of your own.
      </div>
      <div class="d-flex align-items-center gap-2">
        <a
          class="btn btn-sm btn-warning"
          [routerLink]="['/my-account']"
          (click)="dismiss()"
        >
          Change password
        </a>
        <button
          type="button"
          class="btn-close"
          (click)="dismiss()"
          aria-label="Dismiss"
        ></button>
      </div>
    </div>
    }
  `,
})
export class PasswordPromptComponent {
  private authService = inject(AuthService);

  /** Dismissed for this tab only — the prompt returns on the next sign-in. */
  private dismissed = sessionStorage.getItem('pwdPromptDismissed') === '1';
  private user: any = null;

  constructor() {
    this.authService.user$.subscribe((user) => (this.user = user));
  }

  get show(): boolean {
    return (
      this.authService.isLoggedIn() &&
      !!this.user?.mustChangePassword &&
      !this.dismissed
    );
  }

  dismiss(): void {
    this.dismissed = true;
    sessionStorage.setItem('pwdPromptDismissed', '1');
  }
}
